// @ts-nocheck
import { ExclamationTriangleIcon, EyeIcon, PencilIcon } from "@heroicons/react/24/outline";
import {
  ActionIcon,
  Affix,
  Alert,
  Box,
  Button,
  Group,
  List,
  Loader,
  Paper,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import { FilterModal } from "../../components/cv-sections/FilterModal";
import { ROUTES } from "../../constants/routes";
import { EditModeProvider, useEditMode } from "../../context/EditModeContext";
import { useValidation } from "../../context/ValidationContext";
import { mockFilterCategories } from "../../data/mockFilters";
import { useCurrentUser } from "../../hooks/queries/useCurrentUser";
import { CVEditorNew } from "../../pages/CVEditorNew";
import { ChatService } from "../../services/chatService";
import { CoursesService } from "../../services/coursesService";
import { EducationService } from "../../services/educationService";
import { ImportReport } from "../../services/importService";
import { LanguageSkillService } from "../../services/languageSkillService";
import { PersonService } from "../../services/personService";
import { UserCV } from "../../types/cv";
import { PersonFilter } from "../../types/filter";
import { useQualificationSummary } from "../QualificationSummary/hooks/useQualificationSummary";
import { QualificationSummaryService } from "../QualificationSummary/services/qualificationSummaryService";
import { CVDownloadMenu } from "./components/CVDownloadMenu";
import { CVSidebar } from "./components/CVSidebar";
import { CV_SECTIONS } from "./constants/cvSections";
import {
  Certifications,
  PersonCertificationService,
} from "./features/Certification";
import { Education } from "./features/Education";
import { Languages } from "./features/Languages";
import { ProfileCard } from "./features/ProfileCard";
import { ProjectExperience } from "./features/ProjectExperience";
import { ProjectExperienceService } from "./features/ProjectExperience/services/projectExperienceService";
import { QualificationSummaries } from "./features/QualificationSummaries";
import { RoleOverview } from "./features/RoleOverview";
import { SkillsOverview } from "./features/SkillsOverview";
import { TrainingCourses } from "./features/TrainingCourses";
import { WorkExperienceService } from "./features/WorkExperience";
import { WorkExperience } from "./features/WorkExperience/components/WorkExperience";

export { useEditMode } from "../../context/EditModeContext";

export function CVEditor({ personId, importReport }: { personId: number; importReport?: ImportReport }) {
  return (
    <EditModeProvider defaultEditMode={true}>
      <CVEditorContent personId={personId} importReport={importReport} />
    </EditModeProvider>
  );
}

function CVEditorContent({ personId, importReport }: { personId: number; importReport?: ImportReport }) {
  const { isEditMode, setIsEditMode } = useEditMode();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [cv, setCV] = useState<UserCV | null>(null);
  const [loading, setLoading] = useState(true);
  const [sectionsLoading, setSectionsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState("personal-details");
  const [selectedFilters, setSelectedFilters] = useState<PersonFilter[]>([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState<string | null>(null);
  const { setValidationResult, setSectionValidating, isValidating } =
    useValidation();
  const [chatMessages, setChatMessages] = useState<Message[]>([]);
  const [photoUpdateLoading, setPhotoUpdateLoading] = useState(false);
  const [skillsRefreshTrigger, setSkillsRefreshTrigger] = useState(0);
  const [lastCVEditedAt, setLastCVEditedAt] = useState(0);
  const notifyProfileCompletionRefetch = () => setLastCVEditedAt(Date.now());
  const [importReportBanner, setImportReportBanner] = useState<ImportReport | null>(importReport ?? null);

  const [sectionLoadingStates, setSectionLoadingStates] = useState({
    workExperience: true,
    education: true,
    certifications: true,
    courses: true,
    languages: true,
    projects: true,
    qualificationSummaries: true,
  });

  const contentRef = useRef<HTMLDivElement>(null);
  const theme = useMantineTheme();
  const intersectionTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);
  const summariesLoadedRef = useRef(false);

  const personService = new PersonService();
  const workExperienceService = new WorkExperienceService();
  const educationService = new EducationService();
  const personCertificationService = new PersonCertificationService();
  const coursesService = new CoursesService();
  const languageSkillService = new LanguageSkillService();
  const projectExperienceService = new ProjectExperienceService();
  const qualificationSummaryService = new QualificationSummaryService();

  const {
    summaries: qualificationSummaries,
    isLoading: summariesLoading,
    updateSummaries: updateQualificationSummaries,
    refreshSummaries: refreshQualificationSummaries,
  } = useQualificationSummary({
    personId,
    initialSummaries: cv?.qualificationSummaries || [],
    onError: fetchCVData,
  });

  const handleQualificationSummariesChange = (summaries: Parameters<typeof updateQualificationSummaries>[0]) => {
    updateQualificationSummaries(summaries);
    notifyProfileCompletionRefetch();
  };

  useEffect(() => {
    if (cv && qualificationSummaries) {
      setCV((prev) =>
        prev
          ? {
            ...prev,
            qualificationSummaries,
          }
          : null
      );
    }
  }, [qualificationSummaries]);

  useEffect(() => {
    if (cv && !summariesLoading && personId > 0 && !summariesLoadedRef.current) {
      console.log("CV data loaded, refreshing summaries once");
      summariesLoadedRef.current = true;

      setTimeout(() => {
        refreshQualificationSummaries();
      }, 300);
    }
  }, [cv?.id]);

  useEffect(() => {
    const sectionElements = CV_SECTIONS.map((section) =>
      document.getElementById(section.id)
    ).filter(Boolean);

    const observerCallback: IntersectionObserverCallback = (entries) => {
      if (intersectionTimeoutRef.current) {
        clearTimeout(intersectionTimeoutRef.current);
      }

      const visibleEntries = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

      if (visibleEntries.length > 0) {
        intersectionTimeoutRef.current = setTimeout(() => {
          const mostVisibleSection = visibleEntries[0].target.id;
          setActiveSection(mostVisibleSection);
        }, 150);
      }
    };

    const observer = new IntersectionObserver(observerCallback, {
      root: contentRef.current,
      rootMargin: "-5% 0px -85% 0px",
      threshold: [0, 0.1, 0.5, 0.8, 1],
    });

    sectionElements.forEach((section) => section && observer.observe(section));

    return () => {
      if (intersectionTimeoutRef.current) {
        clearTimeout(intersectionTimeoutRef.current);
      }
      observer.disconnect();
    };
  }, []);

  async function fetchCVData() {
    setLoading(true);
    setSectionsLoading(true);
    setError(null);

    setSectionLoadingStates({
      workExperience: true,
      education: true,
      certifications: true,
      courses: true,
      languages: true,
      projects: true,
      qualificationSummaries: true,
    });

    if (!personId || isNaN(personId)) {
      setError("Invalid person ID.");
      setLoading(false);
      setSectionsLoading(false);
      return;
    }

    let personData = null;

    try {
      personData = await personService.getPersonById(personId);
    } catch (err) {
      setError("Failed to load user profile.");
      setLoading(false);
      setSectionsLoading(false);
      return;
    }

    const fullName = `${personData.firstName} ${personData.lastName}`;
    const minimalCV: UserCV = {
      id: personData.id.toString(),
      personId: personData.id.toString(),
      name: fullName,
      title: "",
      email: personData.email,
      phone: "",
      profilePhoto: personData.imagePath,
      officeLocation: personData.officeLocationName,
      officeLocationId: personData.officeLocationId,
      projectExperience: [],
      externalRoles: [],
      qualificationSummaries: [],
      languages: [],
      honors: [],
      publications: [],
      positions: [],
      presentations: [],
      courses: [],
      certifications: [],
      workExperience: [],
      education: [],
      skills: [],
    };

    setCV(minimalCV);
    setLoading(false);

    loadSectionData(personId, personData, minimalCV);
  }

  async function loadSectionData(
    personId: number,
    personData: any,
    baseCV: UserCV
  ) {
    const fullName = `${personData.firstName} ${personData.lastName}`;

    workExperienceService
      .getWorkItemsByUserID(personId)
      .then((workExperienceData) => {
        const workExperience = workExperienceData.map((we: any) => {
          // Use UTC methods to extract date parts to avoid timezone issues
          const fromDate = new Date(we.dateStart);
          const fromDateStr = `${fromDate.getUTCFullYear()}-${String(fromDate.getUTCMonth() + 1).padStart(2, '0')}-${String(fromDate.getUTCDate()).padStart(2, '0')}`;

          let toDateStr = "";
          if (we.dateEnd) {
            const toDate = new Date(we.dateEnd);
            toDateStr = `${toDate.getUTCFullYear()}-${String(toDate.getUTCMonth() + 1).padStart(2, '0')}-${String(toDate.getUTCDate()).padStart(2, '0')}`;
          }

          return {
            id: we.id.toString(),
            fromDate: fromDateStr,
            toDate: toDateStr,
            employer: we.employer,
            position: we.position,
            description: we.description,
            technologies: (we.technologies || [])
              .map((tech: any) => ({
                name: tech.technology?.name || tech.name || tech,
                isFavorite: tech.favourite || false,
              }))
              .filter((t) => Boolean(t.name)),
          };
        });

        setCV((prev) => (prev ? { ...prev, workExperience } : null));
        setSectionLoadingStates((prev) => ({ ...prev, workExperience: false }));
      })
      .catch((error) => {
        console.error("Failed to load work experience:", error);
        setSectionLoadingStates((prev) => ({ ...prev, workExperience: false }));
      });

    educationService
      .getDataByPersonID(personId)
      .then((educationData) => {
        const education = educationData.map((edu: any) => {
          // Use UTC methods to avoid timezone issues
          const fromDate = new Date(edu.dateStart);
          const fromYear = fromDate.getUTCFullYear();
          const fromMonth = String(fromDate.getUTCMonth() + 1).padStart(2, '0');
          const fromDateStr = `${fromMonth}/${fromYear}`;

          let toDateStr = "";
          if (edu.dateEnd) {
            const toDate = new Date(edu.dateEnd);
            const toYear = toDate.getUTCFullYear();
            const toMonth = String(toDate.getUTCMonth() + 1).padStart(2, '0');
            toDateStr = `${toMonth}/${toYear}`;
          }

          return {
            id: edu.id.toString(),
            fromDate: fromDateStr,
            toDate: toDateStr,
            school: edu.institution?.name || "",
            degree: edu.degree?.name || "",
            description: "",
          };
        });

        setCV((prev) => (prev ? { ...prev, education } : null));
        setSectionLoadingStates((prev) => ({ ...prev, education: false }));
      })
      .catch((error) => {
        console.error("Failed to load education:", error);
        setSectionLoadingStates((prev) => ({ ...prev, education: false }));
      });

    personCertificationService
      .getDataByPersonID(personId)
      .then((certificationsData) => {
        const certifications = certificationsData.map((cert: any) => {
          // Use UTC methods to avoid timezone issues
          const certDate = new Date(cert.date);
          const year = certDate.getUTCFullYear();
          const month = String(certDate.getUTCMonth() + 1).padStart(2, '0');
          const startDateStr = `${month}/${year}`;

          return {
            id: cert.id.toString(),
            startDate: startDateStr,
            expiryDate: "",
            name: cert.name,
            isStarred: cert.isStarred,
          };
        });

        setCV((prev) => (prev ? { ...prev, certifications } : null));
        setSectionLoadingStates((prev) => ({ ...prev, certifications: false }));
      })
      .catch((error) => {
        console.error("Failed to load certifications:", error);
        setSectionLoadingStates((prev) => ({ ...prev, certifications: false }));
      });

    coursesService
      .getDataByPersonID(personId)
      .then((coursesData) => {
        const courses = coursesData.map((course: any) => ({
          id: course.id.toString(),
          name: course.title || "",
          yearCompleted: course.date.getFullYear().toString(),
          includeInCV: course.isStarred,
        }));

        setCV((prev) => (prev ? { ...prev, courses } : null));
        setSectionLoadingStates((prev) => ({ ...prev, courses: false }));
      })
      .catch((error) => {
        console.error("Failed to load courses:", error);
        setSectionLoadingStates((prev) => ({ ...prev, courses: false }));
      });

    languageSkillService
      .getDataByPersonID(personId)
      .then((languagesData) => {
        const languages = languagesData.map((lang: any) => ({
          id: lang.id.toString(),
          languageId: lang.language?.id || 0,
          name: lang.language?.englishName || "Unknown",
          proficiencyId: lang.proficiency?.id || 0,
          proficiency: lang.proficiency?.level || "Unknown",
          includeInCV: true,
        }));

        setCV((prev) => (prev ? { ...prev, languages } : null));
        setSectionLoadingStates((prev) => ({ ...prev, languages: false }));
      })
      .catch((error) => {
        console.error("Failed to load languages:", error);
        setSectionLoadingStates((prev) => ({ ...prev, languages: false }));
      });

    projectExperienceService
      .getProjectExperiences(personId)
      .then((projectsData) => {
        const projectExperience = projectsData.map((project: any) => ({
          id: project.id.toString(),
          customerId: project.customerId || 0,
          customerName: project.customerName || "",
          internalProjectId: project.internalProjectId || 0,
          personId: project.personId || personId,
          technologies: project.technologies || [],
          info: {
            description: project.info?.description || "",
            name: project.info?.name || "",
          },
          endDate: project.endDate
            ? project.endDate instanceof Date
              ? project.endDate.toISOString()
              : String(project.endDate)
            : "",
          startDate: project.startDate
            ? project.startDate instanceof Date
              ? project.startDate.toISOString()
              : String(project.startDate)
            : "",
          roles: (project.roles || []).map((r: any) => ({
            name: r.name?.toString() || "",
          })),
          responsibilities: project.responsibilities || "",
          allocationPercent: project.allocationPercent || 100,
        }));

        setCV((prev) => (prev ? { ...prev, projectExperience } : null));
        setSectionLoadingStates((prev) => ({ ...prev, projects: false }));
      })
      .catch((error) => {
        console.error("Failed to load projects:", error);
        setSectionLoadingStates((prev) => ({ ...prev, projects: false }));
      });

    qualificationSummaryService
      .getSummariesByPersonId(personId)
      .then((qualificationSummariesData) => {
        const qualificationSummaries = qualificationSummariesData.map(
          (summary: any) => ({
            id: summary.id,
            personId: summary.personId || 0,
            name: summary.name || "",
            description: summary.description || "",
            selectedForCv: summary.selectedForCv === true,
          })
        );

        const title = qualificationSummariesData[0]?.name || "";

        setCV((prev) =>
          prev
            ? {
              ...prev,
              qualificationSummaries,
              title,
            }
            : null
        );
        setSectionLoadingStates((prev) => ({
          ...prev,
          qualificationSummaries: false,
        }));
      })
      .catch((error) => {
        console.error("Failed to load qualification summaries:", error);
        setSectionLoadingStates((prev) => ({
          ...prev,
          qualificationSummaries: false,
        }));
      });

    const skills = (() => {
      const techData = personData.technologies;

      if (!techData) {
        return [];
      }

      let techArray = [];

      if (Array.isArray(techData)) {
        techArray = techData;
      }
      else if (typeof techData === "string") {
        techArray = techData
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t);
      }
      else if (typeof techData === "object") {
        try {
          techArray = Object.values(techData).filter(Boolean);
          if (techArray.length === 0) {
            techArray = Object.keys(techData).filter(Boolean);
          }
        } catch (e) {
        }
      }

      const timestamp = Date.now();

      return techArray.map((tech: any, index: number) => {
        const techName =
          typeof tech === "object" && tech?.name ? tech.name : String(tech);

        return {
          id: `tech-${index}-${timestamp}`,
          name: techName,
          yearsOfExperience: 0,
          category: "Technology competencies",
          includeInCV: true,
        };
      });
    })();

    setCV((prev) => (prev ? { ...prev, skills } : null));

    const checkAllSectionsLoaded = () => {
      setSectionLoadingStates((current) => {
        const allLoaded = Object.values(current).every((loaded) => !loaded);
        if (allLoaded) {
          setSectionsLoading(false);
        }
        return current;
      });
    };

    const checkInterval = setInterval(() => {
      setSectionLoadingStates((current) => {
        const allLoaded = Object.values(current).every((loaded) => !loaded);
        if (allLoaded) {
          setSectionsLoading(false);
          clearInterval(checkInterval);
        }
        return current;
      });
    }, 100);

    setTimeout(() => {
      clearInterval(checkInterval);
      setSectionsLoading(false);
    }, 30000);
  }

  useEffect(() => {
    fetchCVData();
  }, [personId]);

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
      setActiveSection(sectionId);
    }
  };

  const addFilter = (categoryId: string, itemId: string) => {
    if (
      !selectedFilters.some(
        (f) => f.categoryId === categoryId && f.itemId === itemId
      )
    ) {
      setSelectedFilters([
        ...selectedFilters,
        {
          personId: cv?.id || "",
          categoryId,
          itemId,
        },
      ]);
    }
  };

  const removeFilter = (categoryId: string, itemId: string) => {
    setSelectedFilters(
      selectedFilters.filter(
        (f) => !(f.categoryId === categoryId && f.itemId === itemId)
      )
    );
  };

  const filterBadges = selectedFilters
    .map((filter) => {
      const category = mockFilterCategories.find(
        (cat) => cat.id === filter.categoryId
      );
      const item = category?.items.find((item) => item.id === filter.itemId);

      if (!category || !item) return null;

      return {
        id: `${filter.categoryId}-${filter.itemId}`,
        label: item.name,
        category: category.name,
        categoryId: category.id,
        itemId: item.id,
      };
    })
    .filter(Boolean);

  const handleExternalRolesChange = (
    externalRoles: UserCV["externalRoles"]
  ) => {
    setCV((prev) => ({ ...prev, externalRoles }));
  };

  const handleProjectExperienceChange = (
    projectExperience: UserCV["projectExperience"]
  ) => {
    setCV((prev) => ({ ...prev, projectExperience }));
    setTimeout(() => {
      setSkillsRefreshTrigger((prev) => prev + 1);
    }, 1000);
    notifyProfileCompletionRefetch();
  };

  const handleLanguagesChange = async (languages: UserCV["languages"]) => {
    if (!cv) return;

    try {
      setCV((prev) => ({ ...prev, languages }));

      const cvPersonId = parseInt(cv.personId);

      notifications.show({
        title: "Success",
        message: "Languages updated successfully",
        color: "green",
      });

      try {
        const updatedLanguages = await languageSkillService.getDataByPersonID(
          cvPersonId
        );

        const mappedLanguages = updatedLanguages.map((lang: any) => ({
          id: lang.id.toString(),
          languageId: lang.language?.id || 0,
          name: lang.language?.englishName || "Unknown",
          proficiencyId: lang.proficiency?.id || 0,
          proficiency: lang.proficiency?.level || "Unknown",
          includeInCV: true,
        }));

        setCV((prev) =>
          prev
            ? {
              ...prev,
              languages: mappedLanguages,
            }
            : null
        );
      } catch (error) {
        console.error("Error refreshing languages:", error);
      }
    } catch (error) {
      console.error("Error updating languages:", error);
      notifications.show({
        title: "Error",
        message: "Failed to update languages. Please try again.",
        color: "red",
      });

      await fetchCVData();
    }
  };

  const handleCoursesChange = (courses: UserCV["courses"]) => {
    setCV((prev) => ({ ...prev, courses }));
  };

  const handleCertificationsChange = (
    certifications: UserCV["certifications"]
  ) => {
    setCV((prev) => ({ ...prev, certifications }));
    notifyProfileCompletionRefetch();
  };

  const handleWorkExperienceChange = (
    workExperience: UserCV["workExperience"]
  ) => {
    setCV((prev) => ({ ...prev, workExperience }));
    setTimeout(() => {
      setSkillsRefreshTrigger((prev) => prev + 1);
    }, 1000);
    notifyProfileCompletionRefetch();
  };

  const handleEducationChange = (education: UserCV["education"]) => {
    setCV((prev) => ({ ...prev, education }));
    notifyProfileCompletionRefetch();
  };

  const handleSkillsChange = (skills: UserCV["skills"]) => {
    setCV((prev) => ({ ...prev, skills }));
  };

  const handleGenerateSummary = async () => {
    if (!cv) return;
    try {
      const response = await ChatService.getInstance().generateSummary(personId);
      setCV((prev) =>
        prev
          ? {
            ...prev,
            qualificationSummaries:
              prev.qualificationSummaries.length > 0
                ? prev.qualificationSummaries.map((summary, idx) =>
                  idx === 0
                    ? { ...summary, summary: response.summary }
                    : summary
                )
                : [
                  {
                    id: Date.now().toString(),
                    name: "Generated Summary",
                    summary: response.summary,
                    keyPoints: [],
                    includeInCV: true,
                  },
                ],
          }
          : prev
      );
    } catch (error) {
      console.error("Failed to generate summary:", error);
    }
  };

  const handlePhotoUpdate = async (base64Image: string) => {
    if (!cv) return;

    try {
      setPhotoUpdateLoading(true);

      setCV((prevCV) =>
        prevCV
          ? {
            ...prevCV,
            profilePhoto: base64Image,
          }
          : null
      );

      const personId = parseInt(cv.personId);

      const person = await personService.getPersonById(personId);

      if (!person || !person.firstName || !person.lastName || !person.email) {
        throw new Error("Missing required person data for photo update");
      }

      await personService.updatePhoto(personId, {
        firstName: person.firstName,
        lastName: person.lastName,
        email: person.email,
        image: base64Image,
      });

      notifications.show({
        title: "Success",
        message: "Profile photo updated successfully",
        color: "green",
      });
    } catch (error) {
      setCV((prevCV) => {
        const originalPhoto = prevCV?.profilePhoto;
        return prevCV ? { ...prevCV, profilePhoto: originalPhoto } : null;
      });

      // Show notification to user
      notifications.show({
        title: "Error",
        message: "Failed to update profile photo. Please try again.",
        color: "red",
      });
    } finally {
      setPhotoUpdateLoading(false);
    }
  };

  const handlePhotoRemove = async () => {
    if (!cv) return;

    try {
      setPhotoUpdateLoading(true);

      const personId = parseInt(cv.personId);

      setCV((prevCV) =>
        prevCV
          ? {
            ...prevCV,
            profilePhoto: "",
          }
          : null
      );

      await personService.removePhoto(personId);

      notifications.show({
        title: "Success",
        message: "Profile photo removed successfully",
        color: "green",
      });
    } catch (error) {
      try {
        const person = await personService.getPersonById(parseInt(cv.personId));
        setCV((prevCV) =>
          prevCV
            ? {
              ...prevCV,
              profilePhoto: person.imagePath || "",
            }
            : null
        );
      } catch (refreshError) {
      }

      notifications.show({
        title: "Error",
        message: "Failed to remove profile photo. Please try again.",
        color: "red",
      });
    } finally {
      setPhotoUpdateLoading(false);
    }
  };

  const handleUseAccountPhoto = async () => {
    if (!cv) return;

    try {
      setPhotoUpdateLoading(true);

      const personId = parseInt(cv.personId);

      notifications.show({
        title: "Fetching photo",
        message: "Getting your Microsoft account photo...",
        loading: true,
        autoClose: false,
        withCloseButton: false,
        id: "ms-photo-loading",
      });

      const msAccountPhoto = await personService.fetchMicrosoftAccountPhoto(
        personId
      );

      notifications.hide("ms-photo-loading");

      if (!msAccountPhoto) {
        notifications.show({
          title: "No photo found",
          message:
            "No Microsoft account photo found. Your profile photo remains unchanged.",
          color: "yellow",
        });
        setPhotoUpdateLoading(false);
        return;
      }

      setCV((prevCV) =>
        prevCV
          ? {
            ...prevCV,
            profilePhoto: msAccountPhoto,
          }
          : null
      );

      // Show user that we're saving the photo
      notifications.show({
        title: "Saving photo",
        message: "Updating your profile with Microsoft account photo...",
        loading: true,
        autoClose: false,
        withCloseButton: false,
        id: "ms-photo-saving",
      });

      const person = await personService.getPersonById(personId);

      await personService.updatePhoto(personId, {
        firstName: person.firstName,
        lastName: person.lastName,
        email: person.email,
        image: msAccountPhoto,
      });

      notifications.hide("ms-photo-saving");

      notifications.show({
        title: "Success",
        message:
          "Your Microsoft account photo has been set as your profile photo",
        color: "green",
      });
    } catch (error) {
      notifications.hide("ms-photo-loading");
      notifications.hide("ms-photo-saving");

      let errorMessage = "Failed to use Microsoft account photo.";

      if (error instanceof Error) {
        if (error.message.includes("No accounts found")) {
          errorMessage =
            "You need to be logged in. Please refresh the page and log in again.";
        } else if (error.message.includes("consent")) {
          errorMessage =
            "You need to grant permission to access your Microsoft profile photo.";
        } else if (error.message.includes("404")) {
          errorMessage = "No Microsoft account photo found.";
        } else {
          errorMessage += " " + error.message;
        }
      }

      notifications.show({
        title: "Error",
        message: errorMessage,
        color: "red",
      });

      try {
        const person = await personService.getPersonById(parseInt(cv.personId));
        setCV((prevCV) =>
          prevCV
            ? {
              ...prevCV,
              profilePhoto: person.imagePath || "",
            }
            : null
        );
      } catch (refreshError) {
      }
    } finally {
      setPhotoUpdateLoading(false);
    }
  };

  const handleOfficeLocationChange = async (
    officeLocationId: number | null
  ) => {
    if (!cv) return;

    try {
      const personId = parseInt(cv.personId);

      setCV((prevCV) =>
        prevCV
          ? {
            ...prevCV,
            officeLocationId: officeLocationId,
            officeLocation: null,
          }
          : null
      );

      const person = await personService.getPersonById(personId);
      await personService.updatePerson(personId, {
        firstName: person.firstName,
        lastName: person.lastName,
        email: person.email,
        officeLocationId: officeLocationId,
      });

      await fetchCVData();
    } catch (error) {
      console.error("Error updating office location:", error);

      await fetchCVData();

      notifications.show({
        title: "Error",
        message: "Failed to update office location. Please try again.",
        color: "red",
      });
    }
  };

  return (
    <Box
      style={{
        width: "100%",
        display: "flex",
        minHeight: "calc(100vh - 60px)", // Account for AppShell header
      }}
    >
      {/* Sidebar with navigation */}
      <CVSidebar
        activeSection={activeSection}
        onSectionClick={scrollToSection}
      />
      <Box
        ref={contentRef}
        style={{
          flex: 1,
          padding: "32px",
          paddingTop: isMobile ? "76px" : "32px", // extra top padding for fixed nav bar on mobile
          position: "relative",
        }}
      >
        {!cv ? (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "50vh",
            }}
          >
            <Loader size="md" color="brand" />
            <Text mt="md" c="dimmed">
              Loading CV data...
            </Text>
          </Box>
        ) : (
          <Stack gap="lg">
            <Paper
              p="xl"
              radius="md"
              withBorder
              mb="xl"
              id="personal-details"
              style={{ position: "relative" }}
            >
              <ProfileCard
                cv={cv}
                onPhotoUpdate={handlePhotoUpdate}
                onPhotoRemove={handlePhotoRemove}
                onUseAccountPhoto={handleUseAccountPhoto}
                onOfficeLocationChange={handleOfficeLocationChange}
                lastEditedAt={lastCVEditedAt}
              />

              <Group
                gap="xs"
                style={{
                  position: "absolute",
                  top: "24px",
                  right: "24px",
                }}
              >
                <Button
                  size="sm"
                  variant="light"
                  color="brand"
                  leftSection={<EyeIcon width={14} />}
                  onClick={() => navigate(ROUTES.CV_DETAILS(personId))}
                >
                  View Profile
                </Button>
                <CVDownloadMenu
                  personId={parseInt(cv.personId)}
                  userName={cv.name}
                />
              </Group>
            </Paper>

            {importReportBanner?.hasIssues && (
              <Alert
                variant="light"
                color="orange"
                title="Import completed with warnings"
                icon={<ExclamationTriangleIcon width={20} />}
                withCloseButton
                onClose={() => setImportReportBanner(null)}
              >
                <Text size="sm" mb="xs">
                  The following items could not be matched automatically. Please review and add them manually if needed:
                </Text>
                <List size="sm" spacing="xs">
                  {importReportBanner.technologiesNotFound.length > 0 && (
                    <List.Item>
                      <Text span fw={500}>Technologies not found:</Text>{" "}
                      {importReportBanner.technologiesNotFound.join(", ")}
                    </List.Item>
                  )}
                  {importReportBanner.languagesNotFound.length > 0 && (
                    <List.Item>
                      <Text span fw={500}>Languages not found:</Text>{" "}
                      {importReportBanner.languagesNotFound.join(", ")}
                    </List.Item>
                  )}
                  {importReportBanner.languageLevelsNotFound.length > 0 && (
                    <List.Item>
                      <Text span fw={500}>Language levels not found:</Text>{" "}
                      {importReportBanner.languageLevelsNotFound.join(", ")}
                    </List.Item>
                  )}
                  {importReportBanner.warnings.map((warning, i) => (
                    <List.Item key={i}>{warning}</List.Item>
                  ))}
                </List>
              </Alert>
            )}

            <div id="summary-qualifications">
              {sectionLoadingStates.qualificationSummaries ? (
                <Paper p="xl" radius="md" withBorder>
                  <Group mb="md">
                    <Loader size="sm" />
                    <Text fw={600} size="lg">
                      Loading Summary of Qualifications...
                    </Text>
                  </Group>
                </Paper>
              ) : (
                <QualificationSummaries
                  summaries={qualificationSummaries}
                  onChange={handleQualificationSummariesChange}
                  personId={parseInt(cv.personId)}
                  cvId={parseInt(cv.id)}
                  onGenerateSummary={handleGenerateSummary}
                />
              )}
            </div>

            <div id="roles-overview">
              <RoleOverview
                projectExperience={cv.projectExperience}
                externalRoles={cv.externalRoles}
                onExternalRolesChange={handleExternalRolesChange}
              />
            </div>

            <div id="project-experience">
              {sectionLoadingStates.projects ? (
                <Paper p="xl" radius="md" withBorder>
                  <Group mb="md">
                    <Loader size="sm" />
                    <Text fw={600} size="lg">
                      Loading Project Experience...
                    </Text>
                  </Group>
                </Paper>
              ) : (
                <ProjectExperience
                  projects={cv.projectExperience}
                  onChange={handleProjectExperienceChange}
                  userId={parseInt(cv.personId)}
                  cvId={parseInt(cv.id)}
                />
              )}
            </div>

            <div id="skills-overview">
              <SkillsOverview
                projectExperience={cv.projectExperience}
                skills={cv.skills}
                onChange={handleSkillsChange}
                personId={parseInt(cv.personId)}
                defaultView="cloud"
                refreshTrigger={skillsRefreshTrigger}
              />
            </div>

            <div id="education">
              {sectionLoadingStates.education ? (
                <Paper p="xl" radius="md" withBorder>
                  <Group mb="md">
                    <Loader size="sm" />
                    <Text fw={600} size="lg">
                      Loading Education...
                    </Text>
                  </Group>
                </Paper>
              ) : (
                <Education
                  education={cv.education}
                  onChange={handleEducationChange}
                  personId={parseInt(cv.personId)}
                  cvId={parseInt(cv.id)}
                />
              )}
            </div>

            <div id="work-experience">
              {sectionLoadingStates.workExperience ? (
                <Paper p="xl" radius="md" withBorder>
                  <Group mb="md">
                    <Loader size="sm" />
                    <Text fw={600} size="lg">
                      Loading Work Experience...
                    </Text>
                  </Group>
                </Paper>
              ) : (
                <WorkExperience
                  workExperiences={cv.workExperience}
                  onChange={handleWorkExperienceChange}
                  userId={parseInt(cv.personId)}
                  cvId={parseInt(cv.id)}
                />
              )}
            </div>

            <div id="certifications">
              {sectionLoadingStates.certifications ? (
                <Paper p="xl" radius="md" withBorder>
                  <Group mb="md">
                    <Loader size="sm" />
                    <Text fw={600} size="lg">
                      Loading Certifications...
                    </Text>
                  </Group>
                </Paper>
              ) : (
                <Certifications
                  certifications={cv.certifications}
                  onChange={handleCertificationsChange}
                  personId={parseInt(cv.personId)}
                  cvId={parseInt(cv.id)}
                />
              )}
            </div>

            <div id="languages">
              {sectionLoadingStates.languages ? (
                <Paper p="xl" radius="md" withBorder>
                  <Group mb="md">
                    <Loader size="sm" />
                    <Text fw={600} size="lg">
                      Loading Languages...
                    </Text>
                  </Group>
                </Paper>
              ) : (
                <Languages
                  userId={parseInt(cv.personId)}
                  cvId={parseInt(cv.id)}
                  onMutated={notifyProfileCompletionRefetch}
                />
              )}
            </div>

            <div id="training-courses">
              {sectionLoadingStates.courses ? (
                <Paper p="xl" radius="md" withBorder>
                  <Group mb="md">
                    <Loader size="sm" />
                    <Text fw={600} size="lg">
                      Loading Training and Courses...
                    </Text>
                  </Group>
                </Paper>
              ) : (
                <TrainingCourses
                  courses={cv.courses}
                  onChange={handleCoursesChange}
                  userId={parseInt(cv.personId)}
                  cvId={parseInt(cv.id)}
                />
              )}
            </div>
          </Stack>
        )}

        {/* Mode toggle button */}
        <Affix position={{ bottom: 20, right: 20 }}>
          <Tooltip
            label={
              isEditMode ? "Switch to Preview Mode" : "Switch to Edit Mode"
            }
            position="left"
          >
            <ActionIcon
              color="brand"
              variant="filled"
              radius="xl"
              size="xl"
              onClick={toggleEditMode}
            >
              {isEditMode ? <EyeIcon width={24} /> : <PencilIcon width={24} />}
            </ActionIcon>
          </Tooltip>
        </Affix>
      </Box>

      <FilterModal
        opened={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        filterCategories={mockFilterCategories}
        selectedFilters={selectedFilters}
        onAddFilter={addFilter}
        onRemoveFilter={removeFilter}
      />
    </Box>
  );
}

function CVEditorWrapper() {
  const { id } = useParams<{ id?: string }>();
  const location = useLocation();
  const [personId, setPersonId] = useState<number | null>(null);
  const [isNewMode, setIsNewMode] = useState<boolean>(false);
  const { userRole, currentUser, isAuthenticated, updateUserRole } = useAuth();
  const [roleLoading, setRoleLoading] = useState(false);
  const navigate = useNavigate();
  const { data: reactQueryUser, isLoading: userLoading } = useCurrentUser();
  const importReport = (location.state as { importReport?: ImportReport } | null)?.importReport;

  useEffect(() => {
    if (isAuthenticated && !userLoading && !userRole && reactQueryUser) {
      console.log("User role data loaded for CV editor:", reactQueryUser);
      updateUserRole(reactQueryUser);
    }
  }, [isAuthenticated, userRole, reactQueryUser, userLoading]);

  useEffect(() => {
    if (id === "new") {
      setIsNewMode(true);
      setPersonId(null);
    } else if (id) {
      setPersonId(Number(id));
      setIsNewMode(false);
    } else {
      if (reactQueryUser && !userLoading) {
        if (reactQueryUser.personId) {
          setPersonId(reactQueryUser.personId);
          setIsNewMode(false);
        } else {
          setIsNewMode(true);
          setPersonId(null);
        }
      }
    }
  }, [id, reactQueryUser, userLoading]);

  if (!isNewMode && personId === null)
    return (
      <Box
        style={{
          width: "100%",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          <Loader size="xl" color="brand" />
          <Text size="lg" fw={500}>
            Initializing...
          </Text>
          <Text size="sm" c="dimmed">
            Identifying user profile
          </Text>
        </Box>
      </Box>
    );

  return isNewMode ? (
    <CVEditorNew autoFillUserData={!id || id !== "new"} />
  ) : (
    <CVEditor personId={personId!} importReport={importReport} />
  );
}

export default CVEditorWrapper;
